// Meta Pixel helper – thin wrapper around window.fbq with Advanced Matching
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fbq = (...args: any[]) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (typeof window !== 'undefined' && (window as any).fbq) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).fbq(...args);
  }
};

import { linkedinTrackLead, linkedinTrackContact, linkedinTrackSchedule } from './linkedin';

const PIXEL_ID = '958371096636798';

export type UserData = {
  email?: string;
  phone?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  country?: string; // 2-letter ISO, e.g. 'my', 'sa'
  city?: string;
  state?: string;
  zip?: string;
};

const norm = (v?: string) => (v ? v.trim().toLowerCase() : undefined);

// Normalize phone: keep digits only, drop leading zeros (Meta wants country-code prefixed digits)
const normPhone = (v?: string) => {
  if (!v) return undefined;
  const digits = v.replace(/\D+/g, '');
  return digits || undefined;
};

const splitName = (full?: string): { fn?: string; ln?: string } => {
  if (!full) return {};
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) return { fn: parts[0].toLowerCase() };
  return {
    fn: parts[0].toLowerCase(),
    ln: parts.slice(1).join(' ').toLowerCase(),
  };
};

// Build the Advanced Matching object that the Pixel SDK will hash client-side.
// Field names follow Meta's spec: em, ph, fn, ln, country, ct, st, zp.
// See: https://developers.facebook.com/docs/meta-pixel/advanced/advanced-matching
const buildAdvancedMatch = (u?: UserData): Record<string, string> | undefined => {
  if (!u) return undefined;
  const { fn: fnFromFull, ln: lnFromFull } = splitName(u.fullName);
  const out: Record<string, string> = {};
  const em = norm(u.email);
  const ph = normPhone(u.phone);
  const fn = norm(u.firstName) || fnFromFull;
  const ln = norm(u.lastName) || lnFromFull;
  const country = norm(u.country);
  const ct = norm(u.city);
  const st = norm(u.state);
  const zp = norm(u.zip);
  if (em) out.em = em;
  if (ph) out.ph = ph;
  if (fn) out.fn = fn;
  if (ln) out.ln = ln;
  if (country) out.country = country;
  if (ct) out.ct = ct;
  if (st) out.st = st;
  if (zp) out.zp = zp;
  return Object.keys(out).length ? out : undefined;
};

// Persist user_data hints (e.g. country) on the pixel for subsequent events.
export const setPixelUserData = (u: UserData) => {
  const ud = buildAdvancedMatch(u);
  if (ud) fbq('set', 'userData', ud);
};

// GA4 helper – window.gtag is queued in index.html before gtag.js loads
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const gtag = (...args: any[]) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (typeof window !== 'undefined' && (window as any).gtag) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).gtag(...args);
  }
};

export const trackLead = (source: string, userData?: UserData) => {
  const ud = buildAdvancedMatch(userData);
  if (ud) {
    fbq('trackSingle', PIXEL_ID, 'Lead', { source }, { user_data: ud });
  } else {
    fbq('track', 'Lead', { source });
  }
  linkedinTrackLead(source);
  gtag('event', 'generate_lead', { lead_source: source });
};

export const trackContact = (source: string, component?: string, userData?: UserData) => {
  const ud = buildAdvancedMatch(userData);
  if (ud) {
    fbq('trackSingle', PIXEL_ID, 'Contact', { source, component }, { user_data: ud });
  } else {
    fbq('track', 'Contact', { source, component });
  }
  linkedinTrackContact(source, component);
};

export const trackSchedule = (source: string, userData?: UserData) => {
  const ud = buildAdvancedMatch(userData);
  if (ud) {
    fbq('trackSingle', PIXEL_ID, 'Schedule', { source }, { user_data: ud });
  } else {
    fbq('track', 'Schedule', { source });
  }
  linkedinTrackSchedule(source);
};
